import { useEffect, useMemo, useRef, useState } from "react";
import {Upload, CheckCircle2, Loader2, ShieldCheck, FileText, AlertTriangle, XCircle, Clock, ExternalLink, ChevronDown, ChevronUp, } from 'lucide-react';
import toast from "react-hot-toast";
import { useProDossier, type ProDossier, type ProDossierDocKey } from "@/hooks/useProDossier";
import { uploadOneFile } from "@/lib/upload";
import type { JobCategory } from "@/data/jobs";

interface DocFieldDef {
  key: ProDossierDocKey | "cv";
  label: string;
  required: boolean;
  helper?: string;
}

// Pour les auto-entrepreneurs : pas de CV, uniquement les pièces du dossier professionnel
// (carte pro, assurance, diplômes, PSE…). Le CV n'est PAS demandé.
const MNS_DOCS: DocFieldDef[] = [
  { key: "cartePro", label: "Carte professionnelle", required: true, helper: "Carte pro éducateur sportif en cours de validité" },
  { key: "assurancePro", label: "Attestation d'assurance professionnelle (RC Pro)", required: true },
  { key: "diplome", label: "Diplôme principal (BPJEPS AAN, BEESAN, Licence, MNS)", required: true },
  { key: "caepmns", label: "CAEPMNS (recyclage 5 ans)", required: false, helper: "Optionnel — recommandé si en cours de validité" },
  { key: "pse", label: "PSE1 ou PSE2", required: true },
  { key: "recyclagePse", label: "Recyclage PSE1 ou PSE2 (FC annuelle)", required: false, helper: "Optionnel — à fournir si en cours de validité" },
];

const BNSSA_DOCS: DocFieldDef[] = [
  { key: "assurancePro", label: "Attestation d'assurance professionnelle (RC Pro)", required: true },
  { key: "diplome", label: "Diplôme BNSSA", required: true },
  { key: "recyclageFcBnssa", label: "Recyclage / FC BNSSA (5 ans)", required: false, helper: "Optionnel — à fournir si en cours de validité" },
  { key: "pse", label: "PSE1 ou PSE2", required: true },
  { key: "recyclagePse", label: "Recyclage PSE1 ou PSE2 (FC annuelle)", required: false, helper: "Optionnel — à fournir si en cours de validité" },
];

// Justificatifs entreprise (auto-entrepreneur) — au moins UN des deux est requis
const COMPANY_DOCS: DocFieldDef[] = [
  { key: "kbis", label: "Extrait Kbis", required: false, helper: "Au moins l'un des deux justificatifs (Kbis ou Certificat INSEE) est requis" },
  { key: "certificatInsee", label: "Certificat INSEE (avis SIRENE)", required: false, helper: "Au moins l'un des deux justificatifs (Kbis ou Certificat INSEE) est requis" },
];

const MNS_DIPLOMA_OPTIONS = ["BPJEPS AAN", "BEESAN", "Licence STAPS (mention MNS)", "MNS"];

interface Props {
  userId: string;
  email?: string;
  fullName?: string;
  phone?: string;
  initialDossier: ProDossier | null;
  onChange?: (dossier: ProDossier | null) => void;
}

/**
 * Composant complet d'upload du dossier auto-entrepreneur depuis le tableau de bord candidat.
 * - Si aucun dossier n'existe : permet de choisir la catégorie (BNSSA/MNS), le diplôme (MNS),
 *   et de téléverser TOUTES les pièces possibles (carte pro, assurance, diplômes, PSE, etc.)
 *   ainsi que le CV.
 * - Si un dossier existe : permet de remplacer chaque pièce, mettre à jour le CV, modifier
 *   la catégorie. Tout changement repasse le dossier en attente de validation admin.
 */
export default function AEDossierUploader({
  userId,
  email,
  fullName,
  phone,
  initialDossier,
  onChange,
}: Props) {
  const { upsertDossier, updateDossier } = useProDossier();
  const [dossier, setDossier] = useState<ProDossier | null>(initialDossier);
  const [category, setCategory] = useState<JobCategory>(initialDossier?.category || "MNS");
  const [diplomeType, setDiplomeType] = useState<string>(initialDossier?.diplomeType || "");
  const [files, setFiles] = useState<Partial<Record<ProDossierDocKey | "cv", string>>>(
    (initialDossier?.files as Partial<Record<ProDossierDocKey | "cv", string>>) || {}
  );
  // Informations entreprise (auto-entrepreneur)
  const [siret, setSiret] = useState<string>(initialDossier?.siret || "");
  const [raisonSociale, setRaisonSociale] = useState<string>(initialDossier?.raisonSociale || "");
  const [adresseEntreprise, setAdresseEntreprise] = useState<string>(initialDossier?.adresseEntreprise || "");
  const [telephoneEntreprise, setTelephoneEntreprise] = useState<string>(initialDossier?.telephoneEntreprise || "");
  const [savingCompany, setSavingCompany] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({});
  // Comportement d'ouverture demandé :
  // - 1ère fois (inscription / jamais vu pour CE candidat) : DÉROULÉ.
  // - À chaque nouvelle connexion ensuite : ENROULÉ.
  // On mémorise par utilisateur le fait que la section a déjà été vue (localStorage).
  //
  // IMPORTANT : `userId` peut être vide au tout premier rendu (chargement de la
  // session). On NE doit donc PAS décider l'état initial tant que `userId` n'est
  // pas connu, sinon on écrirait le flag "vu" sous une clé "anon" et l'état
  // resterait figé. On initialise donc à `null` (indéterminé) puis on résout
  // l'état dans un useEffect qui réagit au vrai `userId`.
  const [collapsed, setCollapsed] = useState<boolean | null>(null);
  // Empêche que le toggle manuel de l'utilisateur soit écrasé par la résolution auto.
  const resolvedForUserRef = useRef<string | null>(null);

  useEffect(() => {
    // On attend d'avoir un vrai identifiant utilisateur.
    if (!userId) return;
    // Ne résout qu'une seule fois par utilisateur (évite d'écraser un toggle manuel).
    if (resolvedForUserRef.current === userId) return;
    resolvedForUserRef.current = userId;

    const seenStorageKey = `aeDossier.seen.${userId}`;
    let alreadySeen = false;
    try {
      alreadySeen = window.localStorage.getItem(seenStorageKey) === "1";
    } catch {
      alreadySeen = false;
    }

    // 1ère fois (jamais vu) → déroulé ; sinon (connexions suivantes) → enroulé.
    setCollapsed(alreadySeen);

    // On marque comme "déjà vu" pour que les prochaines connexions soient enroulées.
    try {
      window.localStorage.setItem(seenStorageKey, "1");
    } catch {
      /* localStorage indisponible — sans impact */
    }
  }, [userId]);

  useEffect(() => {
    setDossier(initialDossier);
    if (initialDossier) {
      setCategory(initialDossier.category);
      setDiplomeType(initialDossier.diplomeType || "");
      setFiles((initialDossier.files as Partial<Record<ProDossierDocKey | "cv", string>>) || {});
      setSiret(initialDossier.siret || "");
      setRaisonSociale(initialDossier.raisonSociale || "");
      setAdresseEntreprise(initialDossier.adresseEntreprise || "");
      setTelephoneEntreprise(initialDossier.telephoneEntreprise || "");
    }
  }, [initialDossier]);

  const requiredDocs: DocFieldDef[] = useMemo(
    () => (category === "BNSSA" ? BNSSA_DOCS : MNS_DOCS),
    [category]
  );

  const rawStatus = (dossier as unknown as Record<string, unknown> | null)?.validationStatus as
    | string
    | undefined;
  const status: "pending" | "approved" | "rejected" | "none" =
    rawStatus === "approved"
      ? "approved"
      : rawStatus === "rejected"
      ? "rejected"
      : rawStatus === "pending"
      ? "pending"
      : "none";
  const rejectionReason = (dossier as unknown as Record<string, unknown> | null)?.rejectionReason as
    | string
    | undefined;

  /**
   * Gère l'upload d'un seul document et persiste immédiatement le dossier
   * (création si absent, sinon mise à jour). Cela évite les erreurs où un
   * candidat téléverserait plusieurs fichiers sans cliquer sur "enregistrer".
   */
  const handleFileChange = async (key: ProDossierDocKey | "cv", file?: File) => {
    if (!file) return;
    if (!userId) {
      toast.error("Vous devez être connecté pour téléverser des documents.");
      return;
    }

    setUploading(key);
    try {
      const url = await uploadOneFile(file);
      if (!url) {
        toast.error("L'upload a échoué. Veuillez réessayer.");
        return;
      }

      const newFiles = { ...files, [key]: url };
      setFiles(newFiles);

      setSaving(true);
      let next: ProDossier;
      if (dossier?._id) {
        next = await updateDossier(dossier._id, {
          category,
          diplomeType: diplomeType || undefined,
          files: newFiles,
          fullName,
          email,
          phone,
          siret: siret || undefined,
          raisonSociale: raisonSociale || undefined,
          adresseEntreprise: adresseEntreprise || undefined,
          telephoneEntreprise: telephoneEntreprise || undefined,
        });
      } else {
        next = await upsertDossier(
          {
            category,
            diplomeType: diplomeType || undefined,
            files: newFiles,
            fullName,
            email,
            phone,
            siret: siret || undefined,
            raisonSociale: raisonSociale || undefined,
            adresseEntreprise: adresseEntreprise || undefined,
            telephoneEntreprise: telephoneEntreprise || undefined,
          },
          userId
        );
      }
      setDossier(next);
      onChange?.(next);
      toast.success("Document enregistré. Votre dossier est en attente de validation.");
    } catch (err) {
      console.error("upload doc failed:", err);
      toast.error("Impossible d'enregistrer ce document.");
    } finally {
      setUploading(null);
      setSaving(false);
      if (fileRefs.current[key]) fileRefs.current[key]!.value = "";
    }
  };

  /**
   * Permet de re-soumettre / mettre à jour la catégorie ou le diplôme manuellement.
   */
  const handleSaveMeta = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      let next: ProDossier;
      if (dossier?._id) {
        next = await updateDossier(dossier._id, {
          category,
          diplomeType: diplomeType || undefined,
          files,
          fullName,
          email,
          phone,
          siret: siret || undefined,
          raisonSociale: raisonSociale || undefined,
          adresseEntreprise: adresseEntreprise || undefined,
          telephoneEntreprise: telephoneEntreprise || undefined,
        });
      } else {
        next = await upsertDossier(
          {
            category,
            diplomeType: diplomeType || undefined,
            files,
            fullName,
            email,
            phone,
            siret: siret || undefined,
            raisonSociale: raisonSociale || undefined,
            adresseEntreprise: adresseEntreprise || undefined,
            telephoneEntreprise: telephoneEntreprise || undefined,
          },
          userId
        );
      }
      setDossier(next);
      onChange?.(next);
      toast.success("Dossier mis à jour. En attente de validation.");
    } catch (err) {
      console.error("save meta failed:", err);
      toast.error("Impossible de mettre à jour votre dossier.");
    } finally {
      setSaving(false);
    }
  };

  /**
   * Sauvegarde dédiée des informations entreprise (SIRET, raison sociale, adresse, téléphone).
   * Validation simple côté front : SIRET = 14 chiffres si renseigné.
   */
  const handleSaveCompany = async () => {
    if (!userId) return;
    const trimmedSiret = siret.replace(/\s+/g, "");
    if (trimmedSiret && !/^\d{14}$/.test(trimmedSiret)) {
      toast.error("Le numéro SIRET doit comporter exactement 14 chiffres.");
      return;
    }
    setSavingCompany(true);
    try {
      let next: ProDossier;
      const payload = {
        category,
        diplomeType: diplomeType || undefined,
        files,
        fullName,
        email,
        phone,
        siret: trimmedSiret || undefined,
        raisonSociale: raisonSociale || undefined,
        adresseEntreprise: adresseEntreprise || undefined,
        telephoneEntreprise: telephoneEntreprise || undefined,
      };
      if (dossier?._id) {
        next = await updateDossier(dossier._id, payload);
      } else {
        next = await upsertDossier(payload, userId);
      }
      setSiret(trimmedSiret);
      setDossier(next);
      onChange?.(next);
      toast.success("Informations entreprise enregistrées. En attente de validation.");
    } catch (err) {
      console.error("save company info failed:", err);
      toast.error("Impossible d'enregistrer vos informations entreprise.");
    } finally {
      setSavingCompany(false);
    }
  };

  const statusConfig = {
    pending: {
      icon: Clock,
      bg: "bg-amber-50 border-amber-200",
      iconColor: "text-amber-600",
      badge: "bg-amber-100 text-amber-800",
      label: "En attente de validation",
      hint: "Votre dossier est en cours d'examen. Vous serez notifié par email.",
    },
    approved: {
      icon: CheckCircle2,
      bg: "bg-emerald-50 border-emerald-200",
      iconColor: "text-emerald-600",
      badge: "bg-emerald-100 text-emerald-800",
      label: "Validé",
      hint: "Votre dossier est validé. Vous pouvez postuler aux offres auto-entrepreneur.",
    },
    rejected: {
      icon: XCircle,
      bg: "bg-red-50 border-red-200",
      iconColor: "text-red-600",
      badge: "bg-red-100 text-red-800",
      label: "Refusé — action requise",
      hint: null,
    },
    none: {
      icon: FileText,
      bg: "bg-blue-50 border-blue-200",
      iconColor: "text-blue-600",
      badge: "bg-blue-100 text-blue-800",
      label: "À téléverser",
      hint: "Téléversez tous vos documents pour faire valider votre dossier auto-entrepreneur.",
    },
  } as const;

  const cfg = statusConfig[status];
  const StatusIcon = cfg.icon;

  // `collapsed` peut être `null` (indéterminé tant que userId n'est pas chargé) :
  // on considère alors la section comme déroulée par défaut.
  const isCollapsed = collapsed === true;

  return (
    <section
      id="dossier-ae"
      className={`rounded-2xl border-2 overflow-hidden ${cfg.bg}`}
    >
      {/* Header — cliquable pour enrouler/dérouler */}
      <button
        type="button"
        onClick={() => setCollapsed(() => !isCollapsed)}
        aria-expanded={!isCollapsed}
        aria-controls="dossier-ae-body"
        className="w-full px-5 py-4 flex items-center justify-between gap-3 flex-wrap text-left hover:bg-white/40 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white shadow-sm flex-shrink-0">
            <ShieldCheck className={`w-5 h-5 ${cfg.iconColor}`} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-sm font-bold text-gray-900">
                Mon dossier professionnel Auto-Entrepreneur
              </h2>
              <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full ${cfg.badge}`}>
                <StatusIcon className="inline w-3 h-3 mr-0.5 -mt-px" />
                {cfg.label}
              </span>
            </div>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Catégorie {category}
              {diplomeType && ` · ${diplomeType}`}
              {" · "}Partagé avec toutes vos candidatures auto-entrepreneur
            </p>
          </div>
        </div>
        <span
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-600 bg-white border border-gray-200 rounded-full px-2.5 py-1 flex-shrink-0"
          aria-hidden="true"
        >
          {isCollapsed ? (
            <>
              <ChevronDown className="w-3.5 h-3.5" />
              Dérouler
            </>
          ) : (
            <>
              <ChevronUp className="w-3.5 h-3.5" />
              Enrouler
            </>
          )}
        </span>
      </button>

      {/* Body */}
      {!isCollapsed && (
      <div id="dossier-ae-body" className="px-5 pb-5 space-y-4 bg-white border-t border-gray-100">
        {/* Alert refusé */}
        {status === "rejected" && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-sm font-bold text-red-800 mb-1 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4" />
              Votre dossier a été refusé. Merci de le corriger.
            </p>
            {rejectionReason && (
              <p className="text-xs text-red-700 mt-1">
                <span className="font-semibold">Motif :</span> {rejectionReason}
              </p>
            )}
            <p className="text-[11px] text-red-600 mt-2">
              Remplacez les documents incorrects ci-dessous. Votre dossier sera automatiquement
              re-soumis à validation.
            </p>
          </div>
        )}

        {/* Hint */}
        {status !== "rejected" && cfg.hint && (
          <div className="mt-4 text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2.5 flex items-start gap-2">
            <StatusIcon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${cfg.iconColor}`} />
            {cfg.hint}
          </div>
        )}

        {/* Confidentiality */}
        <div className="mt-4 text-[11px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-start gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-emerald-600" />
          <span>
            Uniquement bnssa-mns.fr aura accès à ces documents, aucunement les recruteurs/clients.
          </span>
        </div>

        {/* Catégorie + diplôme MNS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1">
              Catégorie <span className="text-red-500">*</span>
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as JobCategory)}
              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-xs bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            >
              <option value="MNS">MNS — Maître Nageur Sauveteur</option>
              <option value="BNSSA">BNSSA — Surveillant de baignade</option>
            </select>
          </div>
          {category === "MNS" && (
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                Type de diplôme principal
              </label>
              <select
                value={diplomeType}
                onChange={(e) => setDiplomeType(e.target.value)}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-xs bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              >
                <option value="">— Sélectionner —</option>
                {MNS_DIPLOMA_OPTIONS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {(category !== (dossier?.category || "MNS") ||
          diplomeType !== (dossier?.diplomeType || "")) && (
          <button
            type="button"
            onClick={handleSaveMeta}
            disabled={saving}
            className="text-[11px] font-semibold text-blue-700 hover:underline disabled:opacity-50"
          >
            {saving ? "Enregistrement…" : "Enregistrer la catégorie/diplôme"}
          </button>
        )}

        {/* Documents — Partie 1 : Pièces métier */}
        <div>
          <div className="flex items-center gap-2 mt-4 mb-2">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold">1</span>
            <p className="text-xs font-semibold text-gray-700">
              Pièces justificatives métier
            </p>
          </div>
          <div className="space-y-2">
            {requiredDocs.map((doc) => {
              const url = files[doc.key];
              const isUploading = uploading === doc.key;
              const inputId = `dossier-doc-${doc.key}`;
              return (
                <div
                  key={doc.key}
                  className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5"
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span className="text-xs font-semibold text-gray-700 truncate">
                        {doc.label}{" "}
                        {doc.required ? (
                          <span className="text-red-500">*</span>
                        ) : (
                          <span className="text-gray-400 font-normal">(optionnel)</span>
                        )}
                      </span>
                      {url && (
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 text-[11px] text-blue-600 hover:underline ml-1 flex-shrink-0"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Voir
                        </a>
                      )}
                    </div>

                    <label
                      htmlFor={inputId}
                      className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold cursor-pointer transition-colors flex-shrink-0 ${
                        isUploading
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : url
                          ? "bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-gray-700 hover:text-blue-700"
                          : "bg-blue-600 hover:bg-blue-700 text-white"
                      }`}
                    >
                      {isUploading ? (
                        <><Loader2 className="w-3 h-3 animate-spin" /> Upload…</>
                      ) : url ? (
                        <><Upload className="w-3 h-3" /> Remplacer</>
                      ) : (
                        <><Upload className="w-3 h-3" /> Téléverser</>
                      )}
                    </label>
                    <input
                      id={inputId}
                      ref={(el) => { fileRefs.current[doc.key] = el; }}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      disabled={isUploading || saving}
                      className="sr-only"
                      onChange={(e) => handleFileChange(doc.key, e.target.files?.[0])}
                    />
                  </div>
                  {doc.helper && (
                    <p className="text-[10px] text-gray-500 mt-1">{doc.helper}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Partie 2 — Informations entreprise (auto-entrepreneur) */}
        <div className="mt-5 pt-5 border-t border-gray-200">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold">2</span>
            <p className="text-xs font-semibold text-gray-700">
              Informations entreprise
            </p>
          </div>
          <p className="text-[11px] text-gray-500 mb-3">
            Renseignez les informations administratives de votre activité d'auto-entrepreneur.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                Raison sociale / Dénomination <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={raisonSociale}
                onChange={(e) => setRaisonSociale(e.target.value)}
                placeholder="Ex : Jean Dupont MNS"
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-xs bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                Numéro SIRET <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={siret}
                onChange={(e) => setSiret(e.target.value.replace(/[^\d\s]/g, ""))}
                placeholder="14 chiffres"
                inputMode="numeric"
                maxLength={17}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-xs bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-mono"
              />
              <p className="text-[10px] text-gray-400 mt-1">14 chiffres, sans espaces</p>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                Téléphone professionnel <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={telephoneEntreprise}
                onChange={(e) => setTelephoneEntreprise(e.target.value)}
                placeholder="06 12 34 56 78"
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-xs bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                Adresse de l'entreprise (siège social) <span className="text-red-500">*</span>
              </label>
              <textarea
                value={adresseEntreprise}
                onChange={(e) => setAdresseEntreprise(e.target.value)}
                placeholder="N°, rue, code postal, ville"
                rows={2}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-xs bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
              />
            </div>
          </div>

          {(siret !== (dossier?.siret || "") ||
            raisonSociale !== (dossier?.raisonSociale || "") ||
            adresseEntreprise !== (dossier?.adresseEntreprise || "") ||
            telephoneEntreprise !== (dossier?.telephoneEntreprise || "")) && (
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={handleSaveCompany}
                disabled={savingCompany}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {savingCompany ? (
                  <><Loader2 className="w-3 h-3 animate-spin" /> Enregistrement…</>
                ) : (
                  <>Enregistrer les informations entreprise</>
                )}
              </button>
              <span className="text-[10px] text-gray-500">
                Les modifications repassent le dossier en attente de validation.
              </span>
            </div>
          )}

          {/* Justificatifs entreprise (Kbis / INSEE) */}
          <p className="text-[11px] font-semibold text-gray-700 mt-5 mb-2">
            Justificatif d'immatriculation <span className="text-red-500">*</span>
          </p>
          <p className="text-[10px] text-gray-500 mb-2">
            Fournissez au moins l'un des deux documents suivants.
          </p>
          <div className="space-y-2">
            {COMPANY_DOCS.map((doc) => {
              const url = files[doc.key as ProDossierDocKey];
              const isUploading = uploading === doc.key;
              const inputId = `dossier-doc-${doc.key}`;
              return (
                <div
                  key={doc.key}
                  className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5"
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span className="text-xs font-semibold text-gray-700 truncate">
                        {doc.label}
                      </span>
                      {url && (
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 text-[11px] text-blue-600 hover:underline ml-1 flex-shrink-0"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Voir
                        </a>
                      )}
                    </div>

                    <label
                      htmlFor={inputId}
                      className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold cursor-pointer transition-colors flex-shrink-0 ${
                        isUploading
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : url
                          ? "bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-gray-700 hover:text-blue-700"
                          : "bg-blue-600 hover:bg-blue-700 text-white"
                      }`}
                    >
                      {isUploading ? (
                        <><Loader2 className="w-3 h-3 animate-spin" /> Upload…</>
                      ) : url ? (
                        <><Upload className="w-3 h-3" /> Remplacer</>
                      ) : (
                        <><Upload className="w-3 h-3" /> Téléverser</>
                      )}
                    </label>
                    <input
                      id={inputId}
                      ref={(el) => { fileRefs.current[doc.key] = el; }}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      disabled={isUploading || saving}
                      className="sr-only"
                      onChange={(e) => handleFileChange(doc.key as ProDossierDocKey, e.target.files?.[0])}
                    />
                  </div>
                  {doc.helper && (
                    <p className="text-[10px] text-gray-500 mt-1">{doc.helper}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <p className="text-[10px] text-gray-400 mt-1">
          Formats acceptés : PDF, JPG, PNG, DOC. Chaque document téléversé soumet automatiquement
          votre dossier à validation. Vos candidatures auto-entrepreneur futures bénéficient du
          dossier validé.
        </p>
      </div>
      )}
    </section>
  );
}
